import { DatePickerFr } from '../ui/DatePickerFr';
import React, { useState } from 'react';
import { Download, Clock, Flame, X, Maximize2 } from 'lucide-react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { toast } from 'sonner';

const MapControls = ({ mapData, onTimelineChange, onHeatmapToggle }) => {
  const [showPanel, setShowPanel] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [timelineDate, setTimelineDate] = useState(new Date().toISOString().split('T')[0]);
  const [heatmapEnabled, setHeatmapEnabled] = useState(false);
  
  // Export PDF
  const handleExportPDF = async () => {
    setIsExporting(true);
    toast.info('Génération du PDF en cours...');
    
    try {
      const mapElement = document.querySelector('.map-container');
      
      // Capture de la carte
      const canvas = await html2canvas(mapElement, {
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#0f0c29',
        scale: 2
      });
      
      const imgData = canvas.toDataURL('image/jpeg', 0.8);
      
      // Créer le PDF
      const pdf = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a4'
      });
      
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      
      // Ajouter l'image
      pdf.addImage(imgData, 'JPEG', 0, 0, pdfWidth, pdfHeight);
      
      // Ajouter les métadonnées
      const now = new Date();
      const dateStr = now.toLocaleDateString('fr-FR');
      const timeStr = now.toLocaleTimeString('fr-FR');
      
      pdf.setFontSize(10);
      pdf.setTextColor(0, 255, 255);
      pdf.text(`Carte générée le ${dateStr} à ${timeStr}`, 10, pdfHeight - 5);
      
      // Télécharger
      pdf.save(`carte-monde-${dateStr.replace(/\//g, '-')}.pdf`);
      
      toast.success('PDF généré avec succès !');
    } catch {
      toast.error('Erreur lors de la génération du PDF');
    } finally {
      setIsExporting(false);
    }
  };
  
  // Timeline
  const handleTimelineChange = (date) => {
    setTimelineDate(date);
    if (onTimelineChange) {
      onTimelineChange(date);
    }
    toast.info(`Données filtrées jusqu'au ${new Date(date).toLocaleDateString('fr-FR')}`);
  };
  
  // Heatmap
  const toggleHeatmap = () => {
    const newState = !heatmapEnabled;
    setHeatmapEnabled(newState);
    if (onHeatmapToggle) {
      onHeatmapToggle(newState);
    }
    toast.info(newState ? 'Heatmap activée' : 'Heatmap désactivée');
  };
  
  if (!showPanel) {
    return (
      <button
        onClick={() => setShowPanel(true)}
        className="map-tools-btn"
        title="Outils avancés"
      >
        <Maximize2 className="h-6 w-6" />
      </button>
    );
  }
  
  return (
    <div className="map-advanced-controls">
      <div className="controls-header">
        <h3>🛠️ Outils Avancés</h3>
        <button onClick={() => setShowPanel(false)} className="close-btn">
          <X className="h-5 w-5" />
        </button>
      </div>
      
      <div className="controls-body">
        {/* Export PDF */}
        <div className="control-section">
          <div className="control-icon">
            <Download className="h-6 w-6" />
          </div>
          <div className="control-content">
            <h4>Export PDF</h4>
            <p>Télécharger la carte actuelle</p>
            <button
              onClick={handleExportPDF}
              disabled={isExporting}
              className="action-btn primary"
            >
              {isExporting ? 'Génération...' : 'Télécharger PDF'}
            </button>
          </div>
        </div>
        
        {/* Timeline */}
        <div className="control-section">
          <div className="control-icon">
            <Clock className="h-6 w-6" />
          </div>
          <div className="control-content">
            <h4>Timeline</h4>
            <p>Filtrer par date</p>
            <DatePickerFr
              value={timelineDate}
              onChange={(v) => handleTimelineChange(v)}
              className="date-input w-full"
              minToday={false}
            />
          </div>
        </div>
        
        {/* Heatmap */}
        <div className="control-section">
          <div className="control-icon">
            <Flame className="h-6 w-6" />
          </div>
          <div className="control-content">
            <h4>Heatmap</h4>
            <p>Vue densité thermique</p>
            <button
              onClick={toggleHeatmap}
              className={`action-btn ${heatmapEnabled ? 'danger' : 'secondary'}`}
            >
              {heatmapEnabled ? 'Désactiver' : 'Activer'}
            </button>
          </div>
        </div>
        
        {/* Statistiques */}
        <div className="control-section stats-section">
          <h4>📊 Statistiques</h4>
          <div className="stats-grid">
            <div className="stat-item">
              <span className="stat-label">Total marqueurs</span>
              <span className="stat-value">
                {(mapData.chantiers_rcd?.length || 0) +
                 (mapData.membres?.length || 0) +
                 (mapData.dommages?.length || 0) +
                 (mapData.travaux_reparation?.length || 0) +
                 (mapData.coopers?.length || 0)}
              </span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Dernière MAJ</span>
              <span className="stat-value">
                {new Date(mapData.last_update || Date.now()).toLocaleTimeString('fr-FR', {
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MapControls;
